import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Gift, Euro, Heart, ArrowRight, User, Mail, Clock, Loader2, Phone, Users, Check, X, Tag, ShoppingBag, Plus, Minus, Sparkles } from 'lucide-react';
import { useCart } from '../context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_hilanta-wellness/artifacts/yzd7rzdb_76b87d66-fa32-4fab-93bc-4ef8dc416573.jpg";
const LOGO_CARD_URL = `${BACKEND_URL}/api/email-assets/logo.png`; // Processed circular logo, no black edges


// Couleurs par catégorie (mêmes que Services.jsx - alignées sur le flyer Hilanta)
const CATEGORY_COLORS = {
  energetique: {
    title: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    borderSelected: 'border-emerald-600',
    price: 'text-emerald-700'
  },
  signature: {
    title: 'text-terracotta',
    bg: 'bg-terracotta-50',
    border: 'border-terracotta-200',
    borderSelected: 'border-terracotta',
    price: 'text-terracotta'
  },
  reflexologie: {
    title: 'text-[#7B5E3B]',
    bg: 'bg-[#F4ECDE]',
    border: 'border-[#D8C6A3]',
    borderSelected: 'border-[#A87A4A]',
    price: 'text-[#7B5E3B]'
  },
  feminin: {
    title: 'text-[#4A6D5C]',
    bg: 'bg-[#4A6D5C]/10',
    border: 'border-[#4A6D5C]/30',
    borderSelected: 'border-[#4A6D5C]',
    price: 'text-[#4A6D5C]'
  },
  enfants: {
    title: 'text-rose-700',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    borderSelected: 'border-rose-500',
    price: 'text-rose-700'
  },
  naturopathie: {
    title: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    borderSelected: 'border-amber-500',
    price: 'text-amber-700'
  },
  // Legacy
  massage: {
    title: 'text-terracotta',
    bg: 'bg-terracotta-50',
    border: 'border-terracotta-200',
    borderSelected: 'border-terracotta',
    price: 'text-terracotta'
  },
  activite: {
    title: 'text-blue-700',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    borderSelected: 'border-blue-600',
    price: 'text-blue-700'
  }
};

const FLYER_ORDER = ['energetique', 'signature', 'reflexologie', 'feminin', 'enfants', 'naturopathie'];
const CATEGORY_META = {
  energetique:  { title: 'Soins Énergétiques' },
  signature:    { title: 'Soins Signature Bien-être' },
  reflexologie: { title: 'Réflexologie' },
  feminin:      { title: 'Bien-être Féminin' },
  enfants:      { title: 'Bien-être Enfant' },
  naturopathie: { title: 'Conseils en Naturopathie' },
  massage:      { title: 'Massages' },
  activite:     { title: 'Ateliers & Conférences' },
};

const getCategoryColor = (category) => {
  return CATEGORY_COLORS[category] || CATEGORY_COLORS.signature;
};

const GiftCard = () => {
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get('cancelled');
  const navigate = useNavigate();
  const { addItem, itemCount } = useCart();
  
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]); // Multiple services
  const [duoServices, setDuoServices] = useState({}); // Track DUO option per service
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [quantity, setQuantity] = useState(1);
  
  const [formData, setFormData] = useState({
    buyer_name: '',
    buyer_email: '',
    recipient_name: '',
    personal_message: ''
  });

  // ── Promo code state
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);  // {code, amount_off, final_amount, ...}
  const [promoError, setPromoError] = useState('');
  const [validatingPromo, setValidatingPromo] = useState(false);

  // Re-validate the promo whenever the total changes (e.g. user adds/removes a service)
  useEffect(() => {
    if (appliedPromo && getTotalPrice() <= 0) {
      setAppliedPromo(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServices, duoServices]);

  // When the buyer wants more than one card, drop the page-level promo (it will be
  // applied on the /panier page against the full cart total to avoid double-application).
  useEffect(() => {
    if (quantity > 1 && appliedPromo) {
      setAppliedPromo(null);
      setPromoInput('');
      toast.info('Le code promo s\'appliquera dans votre panier');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantity]);

  // Pre-fill the promo code from ?promo= query param (set by the public banner)
  useEffect(() => {
    const fromUrl = searchParams.get('promo');
    if (fromUrl) {
      setPromoInput(fromUrl.toUpperCase());
    }
  }, [searchParams]);

  const handleApplyPromo = async () => {
    setPromoError('');
    const code = promoInput.trim().toUpperCase();
    const total = getTotalPrice();
    if (!code) {
      setPromoError('Saisissez un code');
      return;
    }
    if (total <= 0) {
      setPromoError('Choisissez d\'abord au moins une prestation');
      return;
    }
    setValidatingPromo(true);
    try {
      const res = await axios.post(`${API}/promo-codes/validate`, { code, amount: total });
      setAppliedPromo(res.data);
      toast.success(`Code "${res.data.code}" appliqué : -${res.data.amount_off.toFixed(2)} €`);
    } catch (err) {
      setAppliedPromo(null);
      setPromoError(err.response?.data?.detail || 'Code invalide');
    } finally {
      setValidatingPromo(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoInput('');
    setPromoError('');
  };

  // Calculate total price with DUO options
  const getTotalPrice = () => {
    return selectedServices.reduce((total, service) => {
      const isDuo = duoServices[service.id] || false;
      return total + (isDuo ? service.price * 2 : service.price);
    }, 0);
  };

  // Check if service can have DUO option (Massage Signature only)
  const canHaveDuo = (service) => {
    const name = service.name.toLowerCase();
    return name.includes('signature') && !name.includes('duo');
  };

  // Toggle service selection
  const toggleService = (service) => {
    setSelectedServices(prev => {
      const isSelected = prev.find(s => s.id === service.id);
      if (isSelected) {
        // Remove service and its DUO option
        setDuoServices(d => {
          const newDuo = { ...d };
          delete newDuo[service.id];
          return newDuo;
        });
        return prev.filter(s => s.id !== service.id);
      } else {
        return [...prev, service];
      }
    });
  };

  // Toggle DUO option for a specific service
  const toggleDuo = (serviceId, e) => {
    e.stopPropagation(); // Prevent service toggle
    setDuoServices(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  // Check if a service is selected
  const isServiceSelected = (serviceId) => {
    return selectedServices.some(s => s.id === serviceId);
  };

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        // Filter only paid services (all categories)
        let paidServices = response.data.filter(s => s.price > 0);
        
        // Custom sort to put Bain de Yoni after Massage Silhouette in feminin category
        const feminineOrder = ["Massage femme enceinte", "Massage Silhouette", "Bain de Yoni", "Massage Xiao"];
        paidServices = paidServices.sort((a, b) => {
          if (a.category === 'feminin' && b.category === 'feminin') {
            const indexA = feminineOrder.indexOf(a.name);
            const indexB = feminineOrder.indexOf(b.name);
            if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB;
            }
          }
          return 0;
        });
        
        setServices(paidServices);
      } catch (error) {
        console.error('Error fetching services:', error);
        toast.error('Erreur lors du chargement des services');
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const handleAddToCart = () => {
    if (selectedServices.length === 0) {
      toast.error('Veuillez sélectionner au moins un soin');
      return;
    }
    if (!formData.recipient_name.trim()) {
      toast.error('Veuillez préciser pour qui est cette carte');
      return;
    }
    setAddingToCart(true);
    addItem({
      services: selectedServices.map((s) => ({
        id: s.id,
        name: s.name,
        price: s.price,
        duration: s.duration,
        category: s.category,
      })),
      duoServices: { ...duoServices },
      recipient_name: formData.recipient_name.trim(),
      personal_message: formData.personal_message.trim(),
      unit_total: getTotalPrice(),
      quantity: quantity,
    });
    const qtyLabel = quantity > 1 ? `${quantity} cartes` : 'Carte';
    toast.success(`${qtyLabel} pour ${formData.recipient_name} ajoutée${quantity > 1 ? 's' : ''} au panier`);
    // Reset selection but keep buyer info for the next card
    setSelectedServices([]);
    setDuoServices({});
    setFormData({ ...formData, recipient_name: '', personal_message: '' });
    setAppliedPromo(null);
    setPromoInput('');
    setQuantity(1);
    setAddingToCart(false);
    // Scroll back to the services selection area
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedServices.length === 0) {
      toast.error('Veuillez sélectionner au moins un soin à offrir');
      return;
    }

    // If buyer wants multiple cards, route through the cart so each card gets its own unique code
    if (quantity > 1) {
      if (!formData.recipient_name.trim()) {
        toast.error('Veuillez préciser pour qui sont ces cartes');
        return;
      }
      addItem({
        services: selectedServices.map((s) => ({
          id: s.id,
          name: s.name,
          price: s.price,
          duration: s.duration,
          category: s.category,
        })),
        duoServices: { ...duoServices },
        recipient_name: formData.recipient_name.trim(),
        personal_message: formData.personal_message.trim(),
        unit_total: getTotalPrice(),
        quantity: quantity,
      });
      toast.success(`${quantity} cartes ajoutées au panier`);
      navigate('/carte-cadeau/panier');
      return;
    }
    
    if (!formData.buyer_name || !formData.buyer_email) {
      toast.error('Veuillez remplir votre nom et email');
      return;
    }

    setSubmitting(true);
    try {
      const totalPrice = getTotalPrice();
      
      // Build services list with DUO info
      const servicesForCard = selectedServices.map(service => {
        const isDuo = duoServices[service.id] || false;
        return {
          id: service.id,
          name: isDuo ? `${service.name} (DUO)` : service.name,
          duration: service.duration,
          price: isDuo ? service.price * 2 : service.price,
          is_duo: isDuo
        };
      });
      
      // Build service names for display
      const serviceNames = servicesForCard.map(s => s.name).join(' + ');
      
      // Create gift card with multiple services
      const giftCardResponse = await axios.post(`${API}/gift-cards`, {
        amount: totalPrice,
        services: servicesForCard,
        service_name: serviceNames,
        buyer_name: formData.buyer_name,
        buyer_email: formData.buyer_email,
        recipient_name: formData.recipient_name || '',
        recipient_email: formData.buyer_email,
        personal_message: formData.personal_message,
        message: formData.personal_message || `Carte cadeau : ${serviceNames}`
      });
      
      const giftCard = giftCardResponse.data;
      
      // Create checkout session
      const checkoutResponse = await axios.post(`${API}/gift-cards/checkout`, {
        gift_card_id: giftCard.id,
        origin_url: window.location.origin,
        promo_code: appliedPromo?.code || null,
      });
      
      // Redirect to Stripe — fire Google Ads conversion first
      if (checkoutResponse.data.url) {
        if (typeof window.gtagSendEvent === 'function') {
          window.gtagSendEvent(checkoutResponse.data.url);
        } else {
          window.location.href = checkoutResponse.data.url;
        }
      }
    } catch (error) {
      console.error('Error creating gift card:', error);
      toast.error('Erreur lors de la création de la carte cadeau');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-terracotta" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="gift-card-page">
      <Helmet>
        <title>Carte Cadeau Massage | Hilanta - Offrir un Soin Bien-être</title>
        <meta name="description" content="Offrez un massage ou soin bien-être à vos proches. Carte cadeau Hilanta valable 1 an. Livraison instantanée par email." />
      </Helmet>
      
      {/* Header */}
      <section className="py-8 bg-sand">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Gift className="mx-auto text-terracotta mb-4" size={40} />
          <h1 className="font-heading text-4xl md:text-5xl text-warm-brown mb-4">
            Offrir un Soin
          </h1>
          <p className="font-body text-warm-brown-light">
            Offrez un moment de bien-être à vos proches
          </p>
        </div>
      </section>

      {cancelled === 'true' && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-terracotta-50 border border-terracotta-200 text-terracotta-700 px-4 py-3 rounded-xl">
            Paiement annulé. Vous pouvez réessayer.
          </div>
        </div>
      )}

      {/* Content */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Preview Card */}
            <div className="order-2 lg:order-1">
              {/* Preview card - exact match to official Hilanta design */}
              <div className="sticky top-24">
                <div className="relative overflow-hidden bg-white shadow-xl" style={{ borderRadius: '3px' }}>
                  
                  {/* Full botanical background scattered across card */}
                  <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.18 }}>
                    <svg width="100%" height="100%" viewBox="0 0 400 520" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
                      <path d="M0 500 Q40 440 80 370 Q120 300 150 220" stroke="#A8C5AE" strokeWidth="1.5" fill="none"/>
                      <ellipse cx="20" cy="475" rx="22" ry="7" fill="#BFD4C4" transform="rotate(-50 20 475)"/>
                      <ellipse cx="40" cy="450" rx="20" ry="7" fill="#A8C5AE" transform="rotate(-45 40 450)"/>
                      <ellipse cx="65" cy="415" rx="21" ry="7" fill="#A8C5AE" transform="rotate(-42 65 415)"/>
                      <ellipse cx="58" cy="423" rx="17" ry="6" fill="#BFD4C4" transform="rotate(-52 58 423)"/>
                      <ellipse cx="95" cy="370" rx="20" ry="7" fill="#C0D5BF" transform="rotate(-45 95 370)"/>
                      <ellipse cx="125" cy="320" rx="19" ry="6.5" fill="#BFD4C4" transform="rotate(-48 125 320)"/>
                      <path d="M0 420 Q25 380 45 340 Q65 300 80 250" stroke="#B5CCBA" strokeWidth="1" fill="none"/>
                      <ellipse cx="22" cy="400" rx="16" ry="5.5" fill="#C8D8C5" transform="rotate(-48 22 400)"/>
                      <ellipse cx="42" cy="365" rx="15" ry="5" fill="#BFD4C4" transform="rotate(-45 42 365)"/>
                      <circle cx="12" cy="488" r="4" fill="#D4B5B0" opacity="0.6"/>
                      <circle cx="30" cy="462" r="3.5" fill="#CEADA8" opacity="0.6"/>
                      <circle cx="55" cy="432" r="3" fill="#D4B5B0" opacity="0.55"/>
                      <circle cx="80" cy="393" r="3" fill="#CEADA8" opacity="0.5"/>
                      <path d="M400 20 Q360 80 310 150 Q265 215 230 290" stroke="#A8C5AE" strokeWidth="1.5" fill="none"/>
                      <ellipse cx="380" cy="40" rx="22" ry="7" fill="#BFD4C4" transform="rotate(50 380 40)"/>
                      <ellipse cx="360" cy="65" rx="20" ry="7" fill="#A8C5AE" transform="rotate(45 360 65)"/>
                      <ellipse cx="335" cy="105" rx="21" ry="7" fill="#A8C5AE" transform="rotate(42 335 105)"/>
                      <ellipse cx="305" cy="155" rx="20" ry="7" fill="#C0D5BF" transform="rotate(45 305 155)"/>
                      <ellipse cx="272" cy="210" rx="19" ry="6.5" fill="#BFD4C4" transform="rotate(48 272 210)"/>
                      <path d="M400 90 Q375 140 350 200 Q325 255 305 310" stroke="#B5CCBA" strokeWidth="1" fill="none"/>
                      <circle cx="388" cy="28" r="4" fill="#D4B5B0" opacity="0.6"/>
                      <circle cx="370" cy="52" r="3.5" fill="#CEADA8" opacity="0.6"/>
                      <circle cx="345" cy="88" r="3" fill="#D4B5B0" opacity="0.55"/>
                      <circle cx="318" cy="135" r="3" fill="#CEADA8" opacity="0.5"/>
                      <path d="M400 520 Q365 480 330 440 Q295 400 270 360" stroke="#A8C5AE" strokeWidth="1.2" fill="none"/>
                      <ellipse cx="385" cy="505" rx="18" ry="6" fill="#BFD4C4" transform="rotate(45 385 505)"/>
                      <ellipse cx="360" cy="472" rx="16" ry="5.5" fill="#A8C5AE" transform="rotate(42 360 472)"/>
                      <path d="M0 80 Q20 55 40 30" stroke="#B5CCBA" strokeWidth="1" fill="none"/>
                      <ellipse cx="15" cy="68" rx="12" ry="4" fill="#C8D8C5" transform="rotate(-45 15 68)"/>
                      <ellipse cx="30" cy="48" rx="11" ry="4" fill="#BFD4C4" transform="rotate(-42 30 48)"/>
                      <circle cx="8" cy="75" r="2.5" fill="#D4B5B0" opacity="0.5"/>
                      <ellipse cx="200" cy="390" rx="14" ry="4.5" fill="#C8D8C5" transform="rotate(20 200 390)" opacity="0.5"/>
                      <ellipse cx="180" cy="170" rx="12" ry="4" fill="#BFD4C4" transform="rotate(-15 180 170)" opacity="0.4"/>
                      <circle cx="240" cy="340" r="2.5" fill="#D4B5B0" opacity="0.35"/>
                    </svg>
                  </div>

                  {/* Card content */}
                  <div className="relative z-10 px-10 pt-10 pb-8">
                    {/* Logo - using processed backend endpoint (clean, no black edges) */}
                    <div className="text-center mb-2">
                      <img
                        src={LOGO_CARD_URL}
                        alt="Hilanta"
                        className="mx-auto"
                        style={{ height: '90px', width: '90px', display: 'inline-block', borderRadius: '50%' }}
                      />
                    </div>
                    <div className="text-center mb-8">
                      <span className="font-bold tracking-[0.45em] text-gray-900 text-sm">HILANTA</span>
                    </div>

                    {/* CARTE CADEAU */}
                    <div className="text-center mb-10">
                      <span className="tracking-[0.55em] text-gray-900 font-bold text-xl">CARTE CADEAU</span>
                    </div>

                    {/* Fields - left aligned */}
                    <div className="space-y-4 mb-6">
                      <div>
                        <span className="font-bold text-sm tracking-[0.1em] text-gray-900">PRESTATION : </span>
                        <span className="text-sm text-gray-700 font-normal">
                          {selectedServices.length > 0
                            ? selectedServices.map(s => duoServices[s.id] ? `${s.name} (DUO)` : s.name).join(' + ')
                            : <em className="text-gray-400">Sélectionnez un soin</em>
                          }
                        </span>
                      </div>
                      <div>
                        <span className="font-bold text-sm tracking-[0.1em] text-gray-900">NUMÉRO DE CARTE : </span>
                        <em className="text-xs text-gray-400">Attribué après paiement</em>
                      </div>
                      <div>
                        <span className="font-bold text-sm tracking-[0.1em] text-gray-900">VALABLE JUSQU'AU : </span>
                        <em className="text-xs text-gray-400">1 an à compter de la date d'achat</em>
                      </div>
                    </div>

                    {/* OFFERT PAR / POUR */}
                    <div className="flex gap-6 mb-6">
                      <div className="flex-1">
                        <span className="font-bold text-sm tracking-[0.1em] text-gray-900">OFFERT PAR : </span>
                        <span className="text-sm text-gray-700">{formData.buyer_name || ''}</span>
                      </div>
                      <div className="flex-1">
                        <span className="font-bold text-sm tracking-[0.1em] text-gray-900">POUR : </span>
                        <span className="text-sm text-gray-700">{formData.recipient_name || ''}</span>
                      </div>
                    </div>

                    {formData.personal_message && (
                      <div className="mb-5 border-l-2 border-amber-400 pl-3 py-1">
                        <p className="text-xs italic text-gray-500">&ldquo;{formData.personal_message}&rdquo;</p>
                      </div>
                    )}

                    <hr className="border-gray-300 mb-4" />

                    <div className="text-center space-y-1.5">
                      <p className="italic text-xs text-gray-500">Réservation par téléphone ou sur hilanta.fr</p>
                      <p className="text-xs text-gray-500">58 Avenue Maréchal foch, 13004 Marseille</p>
                      <p className="text-xs text-gray-500">Contact : 09 60 42 18 22</p>
                      <p className="text-xs text-gray-500">la.hilanta@gmail.com</p>
                      <p className="text-xs font-bold text-gray-800 mt-1">Non remboursable, non échangeable</p>
                    </div>
                  </div>
                </div>

                {selectedServices.length > 0 && (
                  <div className="mt-4 bg-terracotta/10 border border-terracotta/30 rounded-xl p-4 text-center">
                    <p className="font-heading text-2xl text-terracotta font-bold">
                      Total : {getTotalPrice()} €
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Form */}
            <div className="order-1 lg:order-2">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Service Selection */}
                <div>
                  <h3 className="font-heading text-xl text-warm-brown mb-2 flex items-center gap-2">
                    <Gift size={20} className="text-terracotta" />
                    Choisissez les soins à offrir
                  </h3>
                  <p className="font-body text-sm text-warm-brown-light mb-4">
                    Vous pouvez sélectionner plusieurs soins, de différentes catégories
                  </p>

                  {(() => {
                    // Group services by category, ordered as on the flyer
                    const servicesByCategory = FLYER_ORDER.reduce((acc, cat) => {
                      const items = services
                        .filter((s) => s.category === cat)
                        .sort((a, b) => {
                          const ga = a.group_order ?? 99;
                          const gb = b.group_order ?? 99;
                          if (ga !== gb) return ga - gb;
                          return (a.duration ?? 0) - (b.duration ?? 0);
                        });
                      if (items.length > 0) acc[cat] = items;
                      return acc;
                    }, {});
                    // Add any orphan service from legacy categories
                    const orphans = services.filter((s) => !FLYER_ORDER.includes(s.category) && s.category !== 'activite');
                    if (orphans.length > 0) {
                      servicesByCategory.signature = [...(servicesByCategory.signature || []), ...orphans];
                    }
                    return (
                      <div className="space-y-8">
                        {FLYER_ORDER.map((cat) => {
                          const items = servicesByCategory[cat];
                          if (!items || items.length === 0) return null;
                          const catColors = CATEGORY_COLORS[cat];
                          const meta = CATEGORY_META[cat];
                          return (
                            <div key={cat} data-testid={`giftcard-category-${cat}`}>
                              <h4 className={`font-heading text-base sm:text-lg ${catColors.title} font-bold uppercase tracking-wide mb-3 flex items-center gap-2`}>
                                <span className={`w-1.5 h-6 rounded-full ${catColors.bg.replace('bg-', 'bg-').replace('/10', '')} border-2 ${catColors.border}`}></span>
                                {meta.title}
                              </h4>
                              <div className="space-y-3">
                                {items.map((service) => {
                                  const colors = catColors;
                                  const isSelected = isServiceSelected(service.id);
                                  const showDuoOption = canHaveDuo(service);
                                  const isDuo = duoServices[service.id] || false;
                                  const displayPrice = isDuo ? service.price * 2 : service.price;

                                  return (
                                    <div
                                      key={service.id}
                                      data-testid={`service-${service.id}`}
                                      onClick={() => toggleService(service)}
                                      className={`p-4 sm:p-5 rounded-xl ${colors.bg} border-2 ${
                                        isSelected ? colors.borderSelected : colors.border
                                      } transition-all cursor-pointer ${isSelected ? 'shadow-md' : 'hover:shadow-sm'}`}
                                    >
                                      <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-3">
                                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                                            isSelected
                                              ? 'bg-terracotta border-terracotta'
                                              : 'border-gray-300 bg-white'
                                          }`}>
                                            {isSelected && <Check size={14} className="text-white" />}
                                          </div>
                                          <div>
                                            <h5 className={`font-heading text-base sm:text-lg ${colors.title} font-semibold`}>
                                              {service.name}
                                              {isSelected && isDuo && <span className="text-sm ml-2">(DUO)</span>}
                                            </h5>
                                            <p className="font-body text-sm text-warm-brown-light flex items-center gap-2 mt-1">
                                              <Clock size={14} />
                                              {service.duration} min
                                            </p>
                                          </div>
                                        </div>
                                        <div className={`font-heading text-xl sm:text-2xl font-bold ${colors.price}`}>
                                          {displayPrice} €
                                          {isSelected && isDuo && <span className="text-sm">(x2)</span>}
                                        </div>
                                      </div>

                                      {/* Option DUO */}
                                      {isSelected && showDuoOption && (
                                        <div className="mt-3 pt-3 border-t border-gray-200">
                                          <button
                                            type="button"
                                            onClick={(e) => toggleDuo(service.id, e)}
                                            className={`relative w-full overflow-hidden rounded-2xl px-4 py-3.5 text-left transition-all duration-300 transform hover:-translate-y-0.5 ${
                                              isDuo
                                                ? 'bg-gradient-to-r from-terracotta via-terracotta-600 to-rose-500 text-white shadow-xl shadow-terracotta/40 ring-2 ring-terracotta/40'
                                                : 'bg-gradient-to-r from-amber-50 via-rose-50 to-orange-50 border-2 border-dashed border-terracotta/40 hover:border-terracotta hover:shadow-lg hover:shadow-terracotta/20'
                                            }`}
                                            data-testid={`duo-toggle-${service.id}`}
                                          >
                                            {/* Shimmer sweep */}
                                            <span className="pointer-events-none absolute inset-0 -translate-x-full animate-[shimmer_2.5s_infinite] bg-gradient-to-r from-transparent via-white/40 to-transparent" style={{ animationDelay: '0.3s' }} />

                                            <div className="relative flex items-center gap-3">
                                              <div className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center ${
                                                isDuo ? 'bg-white/20' : 'bg-gradient-to-br from-terracotta to-rose-500 shadow-md'
                                              }`}>
                                                <Users size={22} className={isDuo ? 'text-white' : 'text-white'} />
                                              </div>

                                              <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                  <span className={`font-heading font-bold text-base ${isDuo ? 'text-white' : 'text-terracotta'}`}>
                                                    {isDuo ? 'Moment à deux activé' : 'Vivez-le à deux'}
                                                  </span>
                                                  {!isDuo && (
                                                    <Sparkles size={16} className="text-amber-500 animate-pulse" />
                                                  )}
                                                  {isDuo && <Check size={18} className="text-white" />}
                                                </div>
                                                <p className={`text-xs mt-0.5 flex items-center gap-1 ${isDuo ? 'text-white/90' : 'text-warm-brown-light'}`}>
                                                  <Heart size={12} className={isDuo ? 'fill-white text-white' : 'fill-rose-400 text-rose-400'} />
                                                  {isDuo
                                                    ? 'Deux personnes profiteront de ce soin ensemble'
                                                    : 'Offrez ce soin en couple, entre amis ou en famille'}
                                                </p>
                                              </div>

                                              <div className={`flex-shrink-0 text-right ${isDuo ? 'text-white' : 'text-terracotta'}`}>
                                                <div className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-bold ${
                                                  isDuo ? 'bg-white/20' : 'bg-terracotta text-white shadow-sm'
                                                }`}>
                                                  {isDuo ? 'Activé' : `+${service.price} €`}
                                                </div>
                                                {!isDuo && (
                                                  <p className="text-[10px] text-warm-brown-light mt-1 whitespace-nowrap">pour 2 personnes</p>
                                                )}
                                              </div>
                                            </div>
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Buyer Info */}
                <div className="bg-sand rounded-xl p-5">
                  <h3 className="font-heading text-xl text-warm-brown mb-4 flex items-center gap-2">
                    <User size={20} className="text-sage" />
                    Vos informations
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="font-body text-warm-brown font-medium mb-2 block">Votre nom (OFFERT PAR) *</Label>
                      <Input
                        type="text"
                        value={formData.buyer_name}
                        onChange={(e) => setFormData({ ...formData, buyer_name: e.target.value })}
                        placeholder="Votre nom"
                        className="rounded-xl border-warm-brown/20 focus:border-terracotta bg-white"
                        data-testid="buyer-name"
                      />
                    </div>
                    <div>
                      <Label className="font-body text-warm-brown font-medium mb-2 block">Nom du bénéficiaire (POUR) *</Label>
                      <Input
                        type="text"
                        value={formData.recipient_name}
                        onChange={(e) => setFormData({ ...formData, recipient_name: e.target.value })}
                        placeholder="Nom de la personne à qui offrir"
                        className="rounded-xl border-warm-brown/20 focus:border-terracotta bg-white"
                        data-testid="recipient-name"
                      />
                    </div>
                    <div>
                      <Label className="font-body text-warm-brown font-medium mb-2 block">Votre email *</Label>
                      <Input
                        type="email"
                        value={formData.buyer_email}
                        onChange={(e) => setFormData({ ...formData, buyer_email: e.target.value })}
                        placeholder="votre@email.com"
                        className="rounded-xl border-warm-brown/20 focus:border-terracotta bg-white"
                        data-testid="buyer-email"
                      />
                      <p className="font-body text-sm text-warm-brown-light mt-2">
                        La carte cadeau sera envoyée à cette adresse email
                      </p>
                    </div>
                  </div>
                </div>

                {/* Personal Message */}
                <div className="bg-sand rounded-xl p-5">
                  <h3 className="font-heading text-xl text-warm-brown mb-4 flex items-center gap-2">
                    <Heart size={20} className="text-terracotta" />
                    Votre petit mot (optionnel)
                  </h3>
                  <div>
                    <textarea
                      value={formData.personal_message}
                      onChange={(e) => setFormData({ ...formData, personal_message: e.target.value })}
                      placeholder="Écrivez un message personnalisé qui apparaîtra sur la carte cadeau..."
                      className="w-full rounded-xl border border-warm-brown/20 focus:border-terracotta bg-white p-4 font-body text-warm-brown min-h-[120px] resize-none"
                      data-testid="personal-message"
                      maxLength={300}
                    />
                    <p className="font-body text-xs text-warm-brown-light mt-2 text-right">
                      {formData.personal_message.length}/300 caractères
                    </p>
                  </div>
                </div>

                {/* Promo Code */}
                {selectedServices.length > 0 && (
                  <div data-testid="promo-section">
                    <h3 className="font-heading text-xl text-warm-brown mb-2 flex items-center gap-2">
                      <Tag size={20} className="text-rose-600" />
                      Code promo (optionnel)
                    </h3>
                    {!appliedPromo ? (
                      <div className="flex flex-col sm:flex-row gap-3 items-stretch">
                        <Input
                          type="text"
                          value={promoInput}
                          onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoError(''); }}
                          placeholder="EX : NOEL2026"
                          className="rounded-xl bg-white border-warm-brown/20 focus:border-terracotta font-mono uppercase"
                          data-testid="promo-input"
                          maxLength={32}
                        />
                        <Button
                          type="button"
                          onClick={handleApplyPromo}
                          disabled={validatingPromo || !promoInput.trim()}
                          variant="outline"
                          className="rounded-xl border-rose-300 text-rose-700 hover:bg-rose-50 px-6"
                          data-testid="promo-apply-btn"
                        >
                          {validatingPromo ? (
                            <Loader2 className="animate-spin" size={18} />
                          ) : (
                            'Appliquer'
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between" data-testid="promo-applied">
                        <div>
                          <div className="flex items-center gap-2 text-emerald-700 font-semibold">
                            <Check size={18} />
                            Code <span className="font-mono">{appliedPromo.code}</span> appliqué
                          </div>
                          <p className="text-sm text-emerald-700/80 mt-1">
                            Réduction : <strong>-{appliedPromo.amount_off.toFixed(2)} €</strong>
                            {appliedPromo.discount_type === 'percentage' && ` (-${appliedPromo.discount_value}%)`}
                          </p>
                        </div>
                        <Button
                          type="button"
                          onClick={handleRemovePromo}
                          variant="ghost"
                          size="sm"
                          className="rounded-full hover:bg-emerald-100 text-emerald-700"
                          data-testid="promo-remove-btn"
                        >
                          <X size={18} />
                        </Button>
                      </div>
                    )}
                    {promoError && (
                      <p className="text-sm text-red-600 mt-2 flex items-center gap-1" data-testid="promo-error">
                        <X size={14} /> {promoError}
                      </p>
                    )}
                  </div>
                )}

                {/* Quantity selector */}
                {selectedServices.length > 0 && (
                  <div className="bg-white border border-warm-brown/15 rounded-xl p-4 flex items-center justify-between flex-wrap gap-3" data-testid="quantity-selector">
                    <div>
                      <p className="font-heading text-warm-brown text-base">Quantité</p>
                      <p className="text-xs text-warm-brown-light">
                        {quantity > 1
                          ? `${quantity} cartes seront générées (codes uniques)`
                          : 'Combien de cartes identiques souhaitez-vous offrir ?'}
                      </p>
                    </div>
                    <div className="inline-flex items-center rounded-full border border-terracotta/30 bg-cream/40 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                        disabled={quantity <= 1}
                        className="px-4 py-2 hover:bg-terracotta/10 text-warm-brown disabled:opacity-40 disabled:cursor-not-allowed transition"
                        aria-label="Diminuer la quantité"
                        data-testid="qty-decrement"
                      >
                        <Minus size={16} />
                      </button>
                      <span className="px-5 py-2 font-semibold text-warm-brown min-w-[3rem] text-center" data-testid="qty-value">
                        {quantity}
                      </span>
                      <button
                        type="button"
                        onClick={() => setQuantity((q) => Math.min(20, q + 1))}
                        disabled={quantity >= 20}
                        className="px-4 py-2 hover:bg-terracotta/10 text-warm-brown disabled:opacity-40 disabled:cursor-not-allowed transition"
                        aria-label="Augmenter la quantité"
                        data-testid="qty-increment"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Total recap */}
                {selectedServices.length > 0 && (
                  <div className="bg-terracotta/10 border border-terracotta/30 rounded-xl p-4 space-y-1" data-testid="total-recap">
                    {quantity > 1 && (
                      <div className="flex justify-between text-sm text-warm-brown-light">
                        <span>{quantity} × {getTotalPrice().toFixed(2)} € (carte unitaire)</span>
                        <span>{(getTotalPrice() * quantity).toFixed(2)} €</span>
                      </div>
                    )}
                    {appliedPromo && quantity === 1 && (
                      <>
                        <div className="flex justify-between text-sm text-warm-brown-light">
                          <span>Sous-total</span>
                          <span>{getTotalPrice().toFixed(2)} €</span>
                        </div>
                        <div className="flex justify-between text-sm text-emerald-700 font-medium">
                          <span>Code {appliedPromo.code}</span>
                          <span>-{appliedPromo.amount_off.toFixed(2)} €</span>
                        </div>
                        <div className="border-t border-terracotta/20 pt-1 mt-1" />
                      </>
                    )}
                    <div className="flex justify-between items-baseline">
                      <span className="font-heading text-warm-brown">
                        {quantity > 1 ? 'Total estimé' : 'Total à payer'}
                      </span>
                      <span className="font-heading text-2xl text-terracotta font-bold" data-testid="total-final">
                        {quantity > 1
                          ? (getTotalPrice() * quantity).toFixed(2)
                          : (appliedPromo ? appliedPromo.final_amount.toFixed(2) : getTotalPrice().toFixed(2))} €
                      </span>
                    </div>
                    {quantity > 1 && (
                      <p className="text-xs text-warm-brown-light italic pt-1">
                        Le paiement et les codes promo seront finalisés dans votre panier.
                      </p>
                    )}
                  </div>
                )}

                {/* Submit + Add to cart */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button
                    type="button"
                    onClick={handleAddToCart}
                    disabled={addingToCart || selectedServices.length === 0 || !formData.recipient_name.trim()}
                    variant="outline"
                    className="w-full border-2 border-terracotta text-terracotta hover:bg-terracotta hover:text-white rounded-full py-6 text-base font-body"
                    data-testid="add-to-cart-btn"
                  >
                    <ShoppingBag className="mr-2" size={20} />
                    Ajouter au panier
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting || selectedServices.length === 0}
                    className="w-full bg-terracotta hover:bg-terracotta-600 text-white rounded-full py-6 text-base font-body"
                    data-testid="submit-gift-card"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="animate-spin mr-2" size={20} />
                        Traitement...
                      </>
                    ) : quantity > 1 ? (
                      <>
                        <ShoppingBag className="mr-2" size={20} />
                        Continuer ({quantity} cartes — {(getTotalPrice() * quantity).toFixed(2)} €)
                      </>
                    ) : (
                      <>
                        Payer {appliedPromo ? appliedPromo.final_amount.toFixed(2) : getTotalPrice().toFixed(2)} €
                        <ArrowRight className="ml-2" size={20} />
                      </>
                    )}
                  </Button>
                </div>

                {itemCount > 0 && (
                  <Link to="/carte-cadeau/panier" className="block">
                    <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-3 hover:bg-amber-100 transition-colors flex items-center justify-between" data-testid="cart-link-banner">
                      <span className="text-warm-brown font-medium text-sm flex items-center gap-2">
                        <ShoppingBag size={18} className="text-amber-600" />
                        {itemCount} carte{itemCount > 1 ? 's' : ''} dans votre panier
                      </span>
                      <span className="text-amber-700 text-sm font-semibold">Voir le panier →</span>
                    </div>
                  </Link>
                )}

                <p className="font-body text-xs text-center text-warm-brown-light">
                  La carte cadeau sera envoyée à <strong>votre adresse email</strong> immédiatement après le paiement.
                  <br />Vous pourrez ensuite l'imprimer et l'offrir à la personne de votre choix.
                  <br />Valide pendant 1 an.
                  <br /><strong className="text-warm-brown">Carte nominative - Ni remboursable, ni échangeable.</strong>
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-12 bg-sand">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl text-warm-brown text-center mb-8">
            Comment ça fonctionne ?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-terracotta rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold">1</span>
              </div>
              <h3 className="font-heading text-lg text-warm-brown mb-2">Choisissez</h3>
              <p className="font-body text-sm text-warm-brown-light">
                Sélectionnez le soin et ajoutez un petit mot personnalisé
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-sage rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold">2</span>
              </div>
              <h3 className="font-heading text-lg text-warm-brown mb-2">Recevez</h3>
              <p className="font-body text-sm text-warm-brown-light">
                La carte cadeau est envoyée à votre email après paiement
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gold rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold">3</span>
              </div>
              <h3 className="font-heading text-lg text-warm-brown mb-2">Offrez</h3>
              <p className="font-body text-sm text-warm-brown-light">
                Imprimez la carte et offrez-la à la personne de votre choix
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GiftCard;
